import { CardWithBadge, SkeletonCard } from "@endya-conformite/ui-kit";
import { Gauge } from "@src/components/Gauge/Gauge";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import style from "./ValidesData.module.scss";

const getValidesData = <T,>(values: T[], getAvancement: (v: T) => number) => {
  const avancements = values.map(getAvancement);
  return {
    total: values.length,
    valides: avancements.reduce(
      (acc, curr) => (curr === 100 ? acc + 1 : acc),
      0
    ),
  };
};

type ValideDataProps<T> = {
  title: string;
  titleIcon?: JSX.Element;
  linkLabel?: string;
  link?: string;
  values?: T[];
  icon?: JSX.Element;
  getAvancement: (v: T) => number;
  isPerson?: boolean;
  downloadLabel?: string;
  downloadIcon?: JSX.Element;
  onDownload?: () => unknown;
};

export function ValidesData<T>({
  title,
  titleIcon,
  linkLabel,
  link,
  values,
  getAvancement,
  isPerson,
  icon,
  downloadLabel,
  downloadIcon,
  onDownload,
}: ValideDataProps<T>) {
  const validesData = useMemo(
    () => (values ? getValidesData(values, getAvancement) : undefined),
    [values, getAvancement]
  );

  const progression = useMemo(
    () =>
      validesData && validesData.total > 0
        ? (validesData.valides / validesData.total) * 100
        : 0,
    [validesData]
  );

  const isValid =
    validesData?.total === validesData?.valides &&
    (validesData?.valides ?? 0) > 0;

  return (
    <>
      {!values && <SkeletonCard />}
      {values && validesData && (
        <CardWithBadge
          badgeVariant={
            isPerson && validesData.total === 0 ? "error" : "success"
          }
          hasBadge={isPerson && validesData.total === 0}
          label={isPerson ? "Aucune personne renseignée" : ""}
          classNameChildren={style.container}
        >
          <div className={style.header}>
            <h4 className={style.title}>
              {titleIcon && !isValid && (
                <span className={style.titleIcon}>{titleIcon}</span>
              )}
              {title}
            </h4>
            {!isValid &&
              linkLabel &&
              (link ? (
                <Link className={style.link} to={link}>
                  {linkLabel}
                  {icon}
                </Link>
              ) : (
                <span className={style.link}>{linkLabel}</span>
              ))}
            {isValid && downloadLabel && onDownload && (
              <button type="button" className={style.link} onClick={onDownload}>
                {downloadLabel}
                {downloadIcon}
              </button>
            )}
          </div>
          <Gauge isValid={isValid} percent={progression}>
            <div className={style.dataWrapper}>
              <div
                className={style.dataWrapperContent}
                data-len={validesData.valides.toString().length}
              >
                <span
                  className={style.dataScore}
                  data-valid={isValid}
                  data-len={validesData.valides.toString().length}
                >
                  {validesData.valides}
                </span>
                <span className={style.dataTotal}>/ {validesData.total}</span>
              </div>
            </div>
          </Gauge>
        </CardWithBadge>
      )}
    </>
  );
}
